import React, { useRef, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Box, Button, Container, Typography } from "@mui/material";
import StaticPageLayout from "../../components/StaticPageLayout";
import * as styles from "./styles";

type TranslateFnT = (key: string, options?: Record<string, unknown>) => string;

type InViewResultT = {
  ref: React.RefObject<HTMLDivElement>;
  inView: boolean;
};

const useInView = (threshold = 0.15): InViewResultT => {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setInView(true);
      },
      { threshold },
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [threshold]);
  return { ref, inView };
};

type StepT = {
  number: string;
  title: string;
  description: string;
};

type EarningsCardT = {
  key: string;
  amount: string;
  label: string;
  description: string;
};

type AmbassadorContentPropsT = {
  t: TranslateFnT;
};

const AmbassadorContent = ({ t }: AmbassadorContentPropsT) => {
  const { user } = useParams<{ user?: string }>();
  const prefix = user ? `/${user}` : "";
  const { ref: howItWorksRef, inView: howItWorksInView } = useInView();
  const { ref: earningsRef, inView: earningsInView } = useInView();
  const { ref: ctaRef, inView: ctaInView } = useInView();

  const earningsCards: EarningsCardT[] = ["solar", "battery"].map((key) => ({
    key,
    amount: t(`earnings.cards.${key}.amount`),
    label: t(`earnings.cards.${key}.label`),
    description: t(`earnings.cards.${key}.description`),
  }));

  const steps: StepT[] = [
    {
      number: t("howItWorks.steps.step01.number"),
      title: t("howItWorks.steps.step01.title"),
      description: t("howItWorks.steps.step01.description"),
    },
    {
      number: t("howItWorks.steps.step02.number"),
      title: t("howItWorks.steps.step02.title"),
      description: t("howItWorks.steps.step02.description"),
    },
    {
      number: t("howItWorks.steps.step03.number"),
      title: t("howItWorks.steps.step03.title"),
      description: t("howItWorks.steps.step03.description"),
    },
  ];

  const fadeIn = (visible: boolean, delay = 0) => ({
    opacity: visible ? 1 : 0,
    transform: visible ? "translateY(0)" : "translateY(32px)",
    transition: `opacity 0.7s ease ${delay}s, transform 0.7s ease ${delay}s`,
  });

  return (
    <Box>
      <Box sx={styles.heroSection}>
        <Box sx={styles.heroRadialOverlay} />
        <Box sx={styles.heroTopGradient} />
        <Box sx={styles.heroBottomGradient} />
        <Container maxWidth="md" sx={{ position: "relative", zIndex: 1 }}>
          <Typography sx={styles.heroEyebrow}>{t("hero.eyebrow")}</Typography>

          <Typography variant="h1" sx={styles.heroTitle}>
            {t("hero.headlineWhite")} <br />
            <Box component="span" sx={styles.gradientText}>
              {t("hero.headlineGradient")}
            </Box>
          </Typography>

          <Typography sx={styles.heroSubtitle}>{t("hero.subtitle")}</Typography>

          <Typography sx={styles.heroTagline}>{t("hero.tagline")}</Typography>

          <Box sx={{ animation: "fadeUp 0.7s ease 0.6s both" }}>
            <Button
              component={Link}
              to={`${prefix}/ambassador/signup`}
              variant="contained"
              size="large"
              sx={styles.ctaButton}
            >
              {t("hero.ctaButton")}
            </Button>
          </Box>

          <Box sx={styles.heroEarningsLabel}>
            <Box component="span" sx={styles.heroEarningsText}>
              {t("hero.earningsLabel")}
            </Box>
          </Box>
        </Container>
      </Box>

      <Box sx={styles.earningsSection}>
        <Box ref={earningsRef} sx={styles.earningsTextColumn}>
          <Box>
            <Typography sx={{ ...styles.eyebrow, ...fadeIn(earningsInView) }}>{t("earnings.eyebrow")}</Typography>
            <Typography
              variant="h2"
              sx={{
                ...styles.earningsHeading,
                ...fadeIn(earningsInView, 0.15),
              }}
            >
              <Box component="span" sx={styles.gradientText}>
                {t("earnings.headlineGradient")}
              </Box>
              {t("earnings.headlineBase")}
              <br />
              {t("earnings.headlineLine2")}
            </Typography>
            <Typography
              sx={{
                ...styles.earningsDescription,
                ...fadeIn(earningsInView, 0.25),
              }}
            >
              {t("earnings.description")}
            </Typography>

            <Box sx={{ ...styles.earningsCards, ...fadeIn(earningsInView, 0.35) }}>
              {earningsCards.map((card) => (
                <Box key={card.key} sx={styles.earningsCard}>
                  <Typography sx={styles.earningsCardAmount}>{card.amount}</Typography>
                  <Typography sx={styles.earningsCardLabel}>{card.label}</Typography>
                  <Typography sx={styles.earningsCardDescription}>{card.description}</Typography>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>

        <Box
          sx={{
            ...styles.earningsImageWrapper,
            ...fadeIn(earningsInView, 0.3),
          }}
        >
          <Box
            component="img"
            src="/img/assets/Ambassador-4.png"
            alt={t("earnings.imageAlt")}
            sx={styles.earningsImage}
          />
        </Box>
      </Box>

      <Box sx={{ bgcolor: "#0a0f1e", mt: { xs: -8, md: -14 }, pt: 0, pb: { xs: 12, md: 18 } }}>
        <Container maxWidth="lg">
          <Box
            ref={howItWorksRef}
            sx={{
              textAlign: "center",
              mb: 8,
              ...fadeIn(howItWorksInView),
            }}
          >
            <Typography sx={styles.eyebrow}>{t("howItWorks.eyebrow")}</Typography>
            <Typography
              variant="h2"
              sx={{
                ...styles.howItWorksHeading,
                ...fadeIn(howItWorksInView, 0.15),
              }}
            >
              {t("howItWorks.heading")}
            </Typography>
          </Box>

          <Box sx={styles.stepsContainer}>
            <Box sx={styles.stepsConnectorLine} />
            {steps.map((step, i) => (
              <Box
                key={step.number}
                sx={{
                  flex: 1,
                  textAlign: "center",
                  px: { xs: 2, md: 4 },
                  position: "relative",
                  zIndex: 1,
                  ...fadeIn(howItWorksInView, 0.2 + i * 0.15),
                }}
              >
                <Typography sx={styles.stepNumber}>{step.number}</Typography>
                <Typography variant="h6" sx={styles.stepTitle}>
                  {step.title}
                </Typography>
                <Typography sx={styles.stepDescription}>{step.description}</Typography>
              </Box>
            ))}
          </Box>
        </Container>
      </Box>

      <Box sx={styles.ctaSection}>
        <Box sx={styles.ctaTopGradient} />
        <Box sx={styles.ctaRadialOverlay} />
        <Container maxWidth="md" ref={ctaRef} sx={{ position: "relative", zIndex: 2 }}>
          <Typography sx={{ ...styles.eyebrow, ...fadeIn(ctaInView) }}>{t("cta.eyebrow")}</Typography>
          <Typography
            variant="h2"
            sx={{
              ...styles.ctaHeading,
              ...fadeIn(ctaInView, 0.15),
            }}
          >
            {t("cta.headlineBase")}
            <Box component="span" sx={styles.gradientText}>
              {t("cta.headlineGradient")}
            </Box>
            {t("cta.headlineSuffix")}
          </Typography>
          <Typography
            sx={{
              ...styles.ctaDescription,
              ...fadeIn(ctaInView, 0.25),
            }}
          >
            {t("cta.description")}
          </Typography>
          <Box sx={{ ...fadeIn(ctaInView, 0.35) }}>
            <Button
              component={Link}
              to={`${prefix}/ambassador/signup`}
              variant="contained"
              size="large"
              sx={styles.ctaButton}
            >
              {t("cta.button")}
            </Button>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

const AmbassadorPage = () => {
  const { t } = useTranslation("static_ambassador_page");
  return (
    <StaticPageLayout
      title={t("meta.title")}
      description={t("meta.description")}
      canonicalUrl="https://powur.com/ambassador"
      noTopSpacer
    >
      <AmbassadorContent t={t} />
    </StaticPageLayout>
  );
};

export default AmbassadorPage;
